import React, { createContext, useContext, useRef } from "react";
import { useAuth } from "../firebase/AuthProvider"; // Adjust path if needed
import { getUserById } from "../services/userServices/TeamService"; // Adjust path if needed
import { TooltipUser } from "../components/ui/Tooltip/UserProfileTooltip"; // Adjust path if needed

type ProfileCacheEntry = {
  data: TooltipUser | null;
  expiresAt: number;
};

interface UserProfileCacheContextType {
  getUserProfile: (userId: string) => Promise<TooltipUser | null>;
}

const UserProfileCacheContext = createContext<UserProfileCacheContextType | null>(null);

export const UserProfileCacheProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useAuth();
  
  // Store the actual profile data
  const profileCache = useRef<Map<string, ProfileCacheEntry>>(new Map());
  // Store promises for requests that are currently in-flight to prevent duplicates
  const pendingFetches = useRef<Map<string, Promise<TooltipUser | null>>>(new Map());

  const fetchProfile = async (userId: string): Promise<TooltipUser | null> => {
    try {
      const token = await currentUser?.getIdToken();
      if (!token || !userId) return null;

      const res = await getUserById(userId, token);

      if (res?.data) {
        // Cache successful data for 5 minutes
        profileCache.current.set(userId, {
          data: res.data,
          expiresAt: Date.now() + 5 * 60 * 1000, 
        });
        return res.data;
      }
      throw new Error("No data returned");
    } catch (error) {
      console.warn(`[Profile Cache] Failed to fetch profile for ${userId}:`, error);
      
      // Negative Caching: If it fails, cache the null result for 1 minute 
      // so we don't spam the API on repeated hovers.
      profileCache.current.set(userId, {
        data: null,
        expiresAt: Date.now() + 60 * 1000, 
      });
      return null;
    }
  };

  const getUserProfile = async (userId: string): Promise<TooltipUser | null> => {
    const entry = profileCache.current.get(userId);

    // 1. Return cached data if it exists and hasn't expired
    if (entry && entry.expiresAt > Date.now()) {
      return entry.data;
    }

    // 2. If a fetch is already in progress, wait for it instead of starting a new one
    if (pendingFetches.current.has(userId)) {
      return pendingFetches.current.get(userId)!;
    }

    // 3. Start a new fetch
    const fetchPromise = fetchProfile(userId);
    pendingFetches.current.set(userId, fetchPromise);

    const result = await fetchPromise;
    pendingFetches.current.delete(userId); // Cleanup after it resolves

    return result;
  };

  return (
    <UserProfileCacheContext.Provider value={{ getUserProfile }}>
      {children}
    </UserProfileCacheContext.Provider>
  );
};

export const useUserProfileCache = (): UserProfileCacheContextType => {
  const ctx = useContext(UserProfileCacheContext);
  if (!ctx) {
    throw new Error("useUserProfileCache must be used inside UserProfileCacheProvider");
  }
  return ctx;
};